import Papa from "papaparse";

export type RcmRow = Record<
  string,
  string | number | boolean | null | undefined
>;

export type RcmParsed = {
  headers: string[];
  rows: RcmRow[];
};

export type RcmMapping = {
  controlId?: string;
  controlDescription?: string;
  processSystem?: string;
  cycle?: string;
  controlOwner?: string;
};

const textToString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number")
    return Number.isFinite(value) ? `${value}` : "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return `${value}`;
};

const normalizeHeader = (header: string) =>
  header
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const uniqueHeaders = (rows: RcmRow[]): string[] => {
  const set = new Set<string>();
  rows.forEach((row) => Object.keys(row).forEach((k) => set.add(k)));
  return Array.from(set);
};

export async function parseRcmFile(file: File): Promise<RcmParsed> {
  const fileName = file.name.toLowerCase();
  if (fileName.endsWith(".csv")) {
    const text = await file.text();
    const result = Papa.parse(text, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (h) => h?.toString?.().trim(),
    });

    const data = (result.data as Papa.ParseResult<RcmRow>["data"]).map((r) => {
      const obj: RcmRow = {};
      Object.entries(r || {}).forEach(([k, v]) => {
        const key = (k || "").toString().trim();
        if (key) obj[key] = v as unknown as string;
      });
      return obj;
    });

    const headers = result.meta.fields?.filter(Boolean) as string[] | undefined;
    return {
      headers: headers && headers.length > 0 ? headers : uniqueHeaders(data),
      rows: data,
    };
  }

  // Default: parse via xlsx (supports xlsx, xls, xlsm, csv)
  // Dynamically import xlsx only when needed to avoid bundling it in main chunk
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const json = XLSX.utils.sheet_to_json<RcmRow>(worksheet, { defval: "" });
  const rows = json.map((row) => {
    const obj: RcmRow = {};
    Object.entries(row).forEach(([k, v]) => {
      const key = (k || "").toString().trim();
      if (key) obj[key] = v as unknown as string;
    });
    return obj;
  });
  return { headers: uniqueHeaders(rows), rows };
}

type Synonym = { tokens: string[]; weight?: number } | string;

const syn = (value: Synonym): { tokens: string[]; weight: number } =>
  typeof value === "string"
    ? { tokens: [value], weight: 1 }
    : { tokens: value.tokens, weight: value.weight ?? 1 };

const FIELD_SYNONYMS: Record<keyof Required<RcmMapping>, Synonym[]> = {
  controlId: [
    { tokens: ["control uid"], weight: 2 },
    { tokens: ["control id"], weight: 2 },
    "control #",
    "control number",
    "id",
    "reference id",
    "ctrl id",
    "control_id",
  ],
  controlDescription: [
    { tokens: ["control description"], weight: 2 },
    { tokens: ["control"], weight: 2 },
    "description",
    "objective",
    "control objective",
    "control narrative",
  ],
  processSystem: [
    { tokens: ["process/system"], weight: 2 },
    "process system",
    "process",
    "business process",
    "system",
    "process name",
  ],
  cycle: ["cycle", "cycle name", "cycle description"],
  controlOwner: ["control owner", "owner", "responsible party", "owner name"],
};

const scoreHeader = (header: string, synonyms: Synonym[]): number => {
  const norm = normalizeHeader(header);
  let best = 0;
  for (const synItem of synonyms) {
    const { tokens, weight } = syn(synItem);
    for (const token of tokens) {
      const t = normalizeHeader(token);
      if (norm === t) {
        // Exact match - strongly prefer this
        best = Math.max(best, 100 * weight);
      } else if (norm.includes(t)) {
        // Header contains the token (e.g., "control uid" contains "control")
        best = Math.max(best, 5 * weight);
      } else if (t.includes(norm)) {
        // Token contains the header (e.g., "control id" contains "control")
        best = Math.max(best, 3 * weight);
      }
    }
  }
  return best;
};

export function suggestRcmMapping(headers: string[]): RcmMapping {
  const mapping: RcmMapping = {};
  const used = new Set<string>();

  (Object.keys(FIELD_SYNONYMS) as (keyof Required<RcmMapping>)[]).forEach(
    (field) => {
      let bestHeader: string | null = null;
      let bestScore = 0;
      for (const h of headers) {
        if (used.has(h)) continue;
        const s = scoreHeader(h, FIELD_SYNONYMS[field]);
        if (s > bestScore) {
          bestScore = s;
          bestHeader = h;
        }
      }
      if (bestHeader) {
        (mapping as Record<string, string>)[field] = bestHeader;
        used.add(bestHeader);
      }
    },
  );

  return mapping;
}

export type ControlCandidate = {
  controlId: string;
  controlDescription: string;
  description: string;
  processSystem?: string;
  cycle?: string;
  controlOwner?: string;
};

export function buildCandidates(
  parsed: RcmParsed,
  mapping: RcmMapping,
): ControlCandidate[] {
  const m = mapping;
  const rows = parsed.rows;
  return rows
    .map((row) => {
      const controlId = textToString(
        m.controlId ? row[m.controlId] : "",
      ).trim();
      const description = textToString(
        m.controlDescription ? row[m.controlDescription] : "",
      ).trim();
      const processSystem = textToString(
        m.processSystem ? row[m.processSystem] : "",
      ).trim();
      const cycle = textToString(m.cycle ? row[m.cycle] : "").trim();
      const controlOwner = textToString(
        m.controlOwner ? row[m.controlOwner] : "",
      ).trim();
      const emptyRow =
        !controlId && !description && !processSystem && !cycle && !controlOwner;
      if (emptyRow) return null;

      const candidate: ControlCandidate = {
        controlId,
        controlDescription: description,
        description,
        ...(processSystem && { processSystem }),
        ...(cycle && { cycle }),
        ...(controlOwner && { controlOwner }),
      };
      return candidate;
    })
    .filter((candidate) => candidate !== null);
}
